/**
 * UsersManager - Admin component for managing users
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Badge,
  Card,
  Table,
  Dialog,
  TextField,
  Code,
} from '@radix-ui/themes';
import config from '../../config';
import type { User } from '../../types';

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const UsersManager: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);

  // Add-guest-host flow. `created` holds the one-time temp password to hand
  // off after a successful create (never shown again).
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', name: '' });
  const [adding, setAdding] = useState(false);
  const [created, setCreated] = useState<{ email: string; tempPassword: string } | null>(null);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get<{ status: string; data: { user: User } }>(
        `${config.apiBaseUrl}/auth/me`,
        { withCredentials: true }
      );
      setCurrentUserId(response.data.data.user.id);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  }, []);

  const loadUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get<{
        status: string;
        data: { users: User[]; pagination: PaginationInfo };
      }>(`${config.apiBaseUrl}/auth/admin/users`, {
        params: { page, limit: 20 },
        withCredentials: true,
      });
      setUsers(response.data.data.users);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    loadUsers();
  }, [fetchCurrentUser, loadUsers]);

  const handleToggleAdmin = async (user: User): Promise<void> => {
    if (user.id === currentUserId) return;
    setUpdatingUserId(user.id);
    try {
      await axios.patch(
        `${config.apiBaseUrl}/auth/admin/users/${user.id}`,
        { isAdmin: !user.isAdmin },
        { withCredentials: true }
      );
      toast.success(
        user.isAdmin
          ? `Removed admin role from ${user.name || user.email}`
          : `Made ${user.name || user.email} an admin`
      );
      loadUsers(pagination.page);
    } catch (error) {
      console.error('Error updating user:', error);
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Failed to update user');
      } else {
        toast.error('Failed to update user');
      }
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleToggleRole = async (user: User): Promise<void> => {
    if (user.id === currentUserId) return;
    const nextRole = user.role === 'GUEST' ? 'OWNER' : 'GUEST';
    setUpdatingUserId(user.id);
    try {
      await axios.patch(
        `${config.apiBaseUrl}/auth/admin/users/${user.id}`,
        { role: nextRole },
        { withCredentials: true }
      );
      toast.success(
        nextRole === 'OWNER'
          ? `${user.name || user.email} is now an owner`
          : `${user.name || user.email} is now a guest host`
      );
      loadUsers(pagination.page);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Failed to update role');
      } else {
        toast.error('Failed to update role');
      }
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleToggleVerified = async (user: User): Promise<void> => {
    setUpdatingUserId(user.id);
    try {
      await axios.patch(
        `${config.apiBaseUrl}/auth/admin/users/${user.id}`,
        { verified: !user.verified },
        { withCredentials: true }
      );
      toast.success(
        user.verified
          ? `Unverified ${user.name || user.email}`
          : `Verified ${user.name || user.email}`
      );
      loadUsers(pagination.page);
    } catch (error) {
      console.error('Error updating user:', error);
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Failed to update user');
      } else {
        toast.error('Failed to update user');
      }
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleCreateGuest = async (): Promise<void> => {
    if (!addForm.email.trim() || !addForm.name.trim() || adding) return;
    setAdding(true);
    try {
      const res = await axios.post<{ data: { user: User; tempPassword: string } }>(
        `${config.apiBaseUrl}/auth/admin/users`,
        { email: addForm.email.trim(), name: addForm.name.trim() },
        { withCredentials: true }
      );
      setCreated({ email: res.data.data.user.email, tempPassword: res.data.data.tempPassword });
      setAddForm({ email: '', name: '' });
      loadUsers(pagination.page);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Failed to create guest host');
      } else {
        toast.error('Failed to create guest host');
      }
    } finally {
      setAdding(false);
    }
  };

  const closeAdd = () => {
    setShowAdd(false);
    setCreated(null);
    setAddForm({ email: '', name: '' });
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Flex direction="column" gap="4">
      <Flex justify="between" align="start" gap="3" wrap="wrap">
        <Box>
          <Heading size="6">Users</Heading>
          <Text size="2" color="gray">
            Manage operators and invite guest hosts
          </Text>
        </Box>
        <Flex align="center" gap="3">
          <Text size="2" color="gray">
            {pagination.total} user{pagination.total !== 1 ? 's' : ''}
          </Text>
          <Button onClick={() => setShowAdd(true)}>+ Add guest host</Button>
        </Flex>
      </Flex>

      {/* Add-guest-host dialog */}
      <Dialog.Root open={showAdd} onOpenChange={(o) => { if (!o) closeAdd(); }}>
        <Dialog.Content maxWidth="440px">
          {created ? (
            <>
              <Dialog.Title>Guest host created</Dialog.Title>
              <Dialog.Description size="2" color="gray" mb="3">
                Share this one-time password with <strong>{created.email}</strong>. They sign in
                at the login page and can change it after. It won&apos;t be shown again.
              </Dialog.Description>
              <Flex align="center" gap="2" mb="4">
                <Code size="3" style={{ flex: 1, padding: '8px 12px' }}>{created.tempPassword}</Code>
                <Button
                  variant="soft"
                  onClick={() => {
                    navigator.clipboard?.writeText(created.tempPassword);
                    toast.success('Copied');
                  }}
                >
                  Copy
                </Button>
              </Flex>
              <Text size="1" color="gray" as="p" mb="4">
                They can build quizzes, score, and host — but not manage users, edit site content,
                or delete quizzes/venues. Change that anytime from the Role controls.
              </Text>
              <Flex justify="end">
                <Button onClick={closeAdd}>Done</Button>
              </Flex>
            </>
          ) : (
            <>
              <Dialog.Title>Add a guest host</Dialog.Title>
              <Dialog.Description size="2" color="gray" mb="3">
                Creates a verified account in your workspace with build / score / host access.
              </Dialog.Description>
              <Flex direction="column" gap="3">
                <label>
                  <Text as="div" size="1" mb="1" color="gray">Name</Text>
                  <TextField.Root
                    value={addForm.name}
                    placeholder="Sam Guest"
                    onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </label>
                <label>
                  <Text as="div" size="1" mb="1" color="gray">Email</Text>
                  <TextField.Root
                    type="email"
                    value={addForm.email}
                    placeholder="guest@example.com"
                    onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreateGuest(); }}
                  />
                </label>
              </Flex>
              <Flex gap="3" mt="4" justify="end">
                <Dialog.Close>
                  <Button variant="soft" color="gray">Cancel</Button>
                </Dialog.Close>
                <Button
                  onClick={handleCreateGuest}
                  disabled={adding || !addForm.email.trim() || !addForm.name.trim()}
                >
                  {adding ? 'Creating…' : 'Create guest host'}
                </Button>
              </Flex>
            </>
          )}
        </Dialog.Content>
      </Dialog.Root>

      {loading ? (
        <Card>
          <Text color="gray">Loading users...</Text>
        </Card>
      ) : users.length === 0 ? (
        <Card>
          <Flex justify="center" py="6">
            <Text color="gray">No users found.</Text>
          </Flex>
        </Card>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Role</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Verified</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Last Login</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {users.map((user) => {
              const isSelf = user.id === currentUserId;
              const isUpdating = updatingUserId === user.id;
              return (
                <Table.Row key={user.id}>
                  <Table.RowHeaderCell>{user.name || user.email}</Table.RowHeaderCell>
                  <Table.Cell>{user.email}</Table.Cell>
                  <Table.Cell>
                    {user.role === 'GUEST' ? (
                      <Badge variant="soft" color="blue">Guest host</Badge>
                    ) : user.isAdmin ? (
                      <Badge variant="solid" highContrast color="gray">Owner</Badge>
                    ) : (
                      <Badge variant="soft" color="gray">Pending</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {user.verified ? (
                      <Badge variant="soft" color="green">Verified</Badge>
                    ) : (
                      <Badge variant="soft" color="red">Unverified</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>{formatDate(user.lastLoginAt)}</Table.Cell>
                  <Table.Cell>{formatDate(user.createdAt)}</Table.Cell>
                  <Table.Cell>
                    <Flex gap="2" wrap="wrap">
                      <Button
                        size="1"
                        variant="soft"
                        color={user.isAdmin ? 'amber' : 'gray'}
                        onClick={() => handleToggleAdmin(user)}
                        disabled={isSelf || isUpdating}
                        title={isSelf ? 'You cannot change your own admin status' : undefined}
                      >
                        {isUpdating ? '...' : user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                      </Button>
                      {user.isAdmin && (
                        <Button
                          size="1"
                          variant="soft"
                          color={user.role === 'GUEST' ? 'blue' : 'gray'}
                          onClick={() => handleToggleRole(user)}
                          disabled={isSelf || isUpdating}
                          title={isSelf ? 'You cannot change your own role' : undefined}
                        >
                          {isUpdating ? '...' : user.role === 'GUEST' ? 'Make owner' : 'Make guest'}
                        </Button>
                      )}
                      <Button
                        size="1"
                        variant="soft"
                        color={user.verified ? 'gray' : undefined}
                        onClick={() => handleToggleVerified(user)}
                        disabled={isUpdating}
                      >
                        {isUpdating ? '...' : user.verified ? 'Unverify' : 'Verify'}
                      </Button>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      )}

      {pagination.pages > 1 && (
        <Flex align="center" justify="center" gap="3">
          <Button
            variant="soft"
            color="gray"
            size="1"
            disabled={pagination.page <= 1}
            onClick={() => loadUsers(pagination.page - 1)}
          >
            Previous
          </Button>
          <Text size="2" color="gray">
            Page {pagination.page} of {pagination.pages}
          </Text>
          <Button
            variant="soft"
            color="gray"
            size="1"
            disabled={pagination.page >= pagination.pages}
            onClick={() => loadUsers(pagination.page + 1)}
          >
            Next
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default UsersManager;
